import { setLoading } from "./base-element";
import { constants } from "../../ticketing/common";
import { attributes, classes, ecommerceEvents, events } from "../../../scripts/constants";
import { EventEmitter, attribute, getParentElement, postJson, trackCheckoutEvent, trackEcommerceEvent } from "../../../utils";
import { logError } from "../basket-v2/errorReporting";
import {
  Stripe,
  StripeElements,
  StripeElementsOptionsClientSecret,
  StripeAddressElementOptions,
  StripePaymentElementOptions,
  loadStripe,
} from "@stripe/stripe-js";
import { ApiResponseWithData, BasketResponse, SetDeliveryDetailsRequest } from "../../ticketing/basket-v2/api/models";
import { endLoadingState, loadingTemplate, startLoadingState } from "../basket-v2/basketLoading";

let stripe: Stripe;
let elements: StripeElements;
let paymentForm: HTMLFormElement | null;

const elementOptions: StripeElementsOptionsClientSecret = {
  appearance: {
    theme: "stripe",
    labels: "floating",
    variables: {
      colorBackground: "transparent",
      colorPrimary: "#0f2d2d",
      borderRadius: "0",
      spacingGridRow: "30px",
      fontFamily: "ariana-pro",
    },
    rules: {
      ".AccordionItem": {
        backgroundColor: "transparent",
        color: "#0f2d2d",
        border: "none",
        boxShadow: "none",
        paddingTop: "16px !important",
      },
      ".Tab": {
        color: "#0f2d2d",
      },
      ".TabLabel": {
        color: "#0f2d2d",
      },
      ".Label": {
        color: "#d5d5d5",
      },
      ".Label--floating": {
        color: "#0f2d2d",
      },
      ".Input": {
        border: "1px solid #d5d5d5",
        boxShadow: "inset 0 0 0 1px #dedede",
        fontSize: "16px",
        padding: "var(--p-spacing2)",
        paddingLeft: "20px",
        lineHeight: "20px",
        color: "#0f2d2d",
      },
      ".Input:focus": {
        outline: "2px solid #0f2d2d",
        boxShadow: "none",
        borderColor: "transparent",
        outlineOffset: "-1px",
        borderRadius: "2px",
      },
    },
  },
  fonts: [
    {
      family: "ariana-pro",
      src: 'url("https://use.typekit.net/af/1e9eba/00000000000000007735cbdf/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n5&v=3")',
      weight: "500",
      style: "normal",
    },
  ],
};

const addressElementOptions: StripeAddressElementOptions = {
  mode: "shipping",
  //allowedCountries: ['GB'],
  autocomplete: {
    mode: "google_maps_api",
    apiKey: "",
  },
  display: {
    name: "split",
  },
  fields: {
    phone: "always",
  },
  validation: {
    phone: {
      required: "always",
    },
  },
};

const paymentElementOptions: StripePaymentElementOptions = {
  layout: "accordion",
  fields: {
    billingDetails: {
      name: "never",
      email: "never",
      phone: "never",
      address: "never",
    },
  },
};

let config = {
  checkoutElementSelector: ".checkout",
  addressElementSelector: "#address-element",
  paymentElementSelector: "#payment-element",
  loadingClass: "is-loading",
  paymentFormSelector: "#payment-form",
  orderTotalSelector: "[data-order-total]",
  emailInputSelector: "#email",
  dobDaySelector: "#day",
  dobMonthSelector: "#month",
  dobYearSelector: "#year",
  extraFieldsSelector: "[data-extra-fields]",
  redirectUri: ''
}

let detailsRequest: SetDeliveryDetailsRequest = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  addressLine1: "",
  addressLine2: "",
  townCity: "",
  postalCode: "",
  country: "GB",
  setExtraFieldsRequest: {
    order: {},
    tickets: {}
  }
};

const processingPaymentMessage =
  '<div class="notice-overlay__content"> <div class="section-title-wrapper"> <h2 class="notice-overlay__title section-title u-text-center"> <span>Processing your payment</span> </h2> </div> <div class="indicator"> <div class="icon"> <div></div> <div></div> <div></div> <div></div> <div></div> </div> </div> <p class="notice-overlay__message">Please don\'t close or refresh this window whilst we process your order.</p> </div>';

const processingOrderUpdateMessage = loadingTemplate("Updating order...");
const checkoutElement = document.querySelector<HTMLElement>(config.checkoutElementSelector);

export async function initStripePaymentElement() {
  await initialize();

  EventEmitter.on(events.ORDER_UPDATE_START, async () => {
    startLoadingState(processingOrderUpdateMessage, ".basket-overlay");
  });

  EventEmitter.on(events.ORDER_UPDATE_END, async () => {
    await elements.fetchUpdates();
    endLoadingState(".basket-overlay");
  });
}

async function initialize() {
  paymentForm = document.querySelector<HTMLFormElement>(config.paymentFormSelector);

  if (!paymentForm) {
    return;
  }

  paymentForm.addEventListener("submit", handleSubmit);

  const emailInput = paymentForm.querySelector<HTMLInputElement>(config.emailInputSelector);
  emailInput.addEventListener("change", () => {
    detailsRequest.email = emailInput.value;
  });
  
  const dateOfBirthElement = paymentForm.querySelector<HTMLElement>("[data-date-of-birth]");

  const dobDayInput = dateOfBirthElement.querySelector<HTMLInputElement>(config.dobDaySelector);
  const dobMonthInput = dateOfBirthElement.querySelector<HTMLInputElement>(config.dobMonthSelector);
  const dobYearInput = dateOfBirthElement.querySelector<HTMLInputElement>(config.dobYearSelector);

  const updateDOB = () => {
    const day = parseInt(dobDayInput.value, 10);
    const month = parseInt(dobMonthInput.value, 10);
    const year = parseInt(dobYearInput.value, 10);

    detailsRequest.dobDay = day;
    detailsRequest.dobMonth = month;
    detailsRequest.dobYear = year;
    
    const allFilled =
      dobDayInput.value.trim() !== "" &&
      dobMonthInput.value.trim() !== "" &&
      dobYearInput.value.trim() !== "";

    if (allFilled) {
      checkDate(day, month, year, dateOfBirthElement);
    }
  };

  [dobDayInput, dobMonthInput, dobYearInput].forEach((input) => {
    input.addEventListener("change", updateDOB);
  });

  const extraFieldsWrapper = paymentForm.querySelector(config.extraFieldsSelector);

  if(extraFieldsWrapper) {
    const extraFieldsInputs = extraFieldsWrapper.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input, select, textarea');

    extraFieldsInputs.forEach((input) => {    
      input.addEventListener("change", (event) => {
        const target = event.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

        const fieldElement = getParentElement(target, '[data-field-type]');
        
        const fieldType = fieldElement.dataset.fieldType;      
        const ticketId = fieldElement.dataset.ticketId;
        const isDateField = fieldType === "date";     
        const isCheckboxField = fieldType === "checkbox";

        let value = target.value;

        const key = fieldElement.dataset.fieldName;

        if(isDateField) {
          const dobDayInputValue = fieldElement.querySelector<HTMLInputElement>('.inline-date__day').value;
          const dobMonthInputValue = fieldElement.querySelector<HTMLInputElement>('.inline-date__month').value;
          const dobYearInputValue = fieldElement.querySelector<HTMLInputElement>('.inline-date__year').value;

          if(dobDayInputValue && dobMonthInputValue && dobYearInputValue) {
            checkDate(parseInt(dobDayInputValue), parseInt(dobMonthInputValue), parseInt(dobYearInputValue), fieldElement, "Please enter a valid date");
            value = `${dobDayInputValue.padStart(2, '0')}/${dobMonthInputValue.padStart(2, '0')}/${dobYearInputValue}`;        
          }        
        }

        if (isCheckboxField) {
          const targetElement = target as HTMLInputElement;
          if (targetElement.checked)
          {
            value = targetElement.value;
          }
          else
          {
            if (ticketId)
            {
              const ticketsObj = detailsRequest.setExtraFieldsRequest.tickets;
              if (ticketsObj && ticketsObj[ticketId]) {
                if (key && ticketsObj[ticketId].hasOwnProperty(key)) {
                  delete ticketsObj[ticketId][key];
                }
                // If the ticket object is now empty, remove the ticket entry
                if (Object.keys(ticketsObj[ticketId] || {}).length === 0) {
                  delete ticketsObj[ticketId];
                }
              }
            }
            else
            {
              const orderObj = detailsRequest.setExtraFieldsRequest.order;
              if (orderObj && key && orderObj.hasOwnProperty(key))
              {
                delete orderObj[key];
              }
            }
            return;
          }
        }

        if (ticketId) {
          detailsRequest.setExtraFieldsRequest.tickets[ticketId] ??= {};
          detailsRequest.setExtraFieldsRequest.tickets[ticketId][key] = value;
        } else {
          detailsRequest.setExtraFieldsRequest.order[key] = value;
        }
      });
    });
  }

  const response = await fetch(constants.urls.payment.create, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    
    showMessage("There was an error initializing the checkout. Please try again later.", { hideOnTimeout: false, wrapperCssClass: "payment-failed--checkout" });

    logError(response.status, response.statusText, "There was an error initializing payment");
    
    document.querySelectorAll<HTMLElement>("#payment-form > div:not(.payment-failed)").forEach((htmlElement) => {
      htmlElement.style.display = "none";
    });

    return;
  }

  var responseJson: any = await response.json();

  addressElementOptions.autocomplete["apiKey"] = responseJson.gMapsApiKey;
  addressElementOptions.allowedCountries = responseJson.allowedCountries;

  config.redirectUri =
    responseJson.redirectUri && responseJson.redirectUri != "" ? responseJson.redirectUri : window.location.href;

  if (responseJson.stripeAccount && responseJson.stripeAccount.length > 0) {
    stripe = await loadStripe(responseJson.apiKey, {
      stripeAccount: responseJson.stripeAccount,
    });
  } else {
    stripe = await loadStripe(responseJson.apiKey);
  }

  checkStatus();

  if (responseJson.clientSecret && responseJson.clientSecret != "") {
    elementOptions.clientSecret = responseJson.clientSecret;
  }

  elements = stripe.elements(elementOptions);

  if (responseJson.accountDetails?.email) {
    emailInput.value = responseJson.accountDetails.email ?? "";
    detailsRequest.email = responseJson.accountDetails.email ?? "";
    detailsRequest.phoneNumber = responseJson.accountDetails.phoneNumber ?? "";
    detailsRequest.firstName = responseJson.accountDetails.firstname ?? "";
    detailsRequest.lastName = responseJson.accountDetails.lastname ?? "";
    detailsRequest.addressLine1 = responseJson.accountDetails.address1 ?? "";
    detailsRequest.addressLine2 = responseJson.accountDetails.address2 ?? "";
    detailsRequest.townCity = responseJson.accountDetails.townCity ?? "";
    detailsRequest.postalCode = responseJson.accountDetails.postcode ?? "";
    detailsRequest.country = responseJson.accountDetails.country ?? "";
    //TODO: DateOfBirth fields

    addressElementOptions.defaultValues = {
      firstName: responseJson.accountDetails.firstname,
      lastName: responseJson.accountDetails.lastname,
      phone: responseJson.accountDetails.phoneNumber,
      address: {
        line1: responseJson.accountDetails.address1,
        line2: responseJson.accountDetails.address2,
        city: responseJson.accountDetails.townCity,
        postal_code: responseJson.accountDetails.postcode,
        country: responseJson.accountDetails.country,
      },
    };

    trackEvent(ecommerceEvents.add_shipping_info);
  }

  const stripeAddressElement = elements.create("address", addressElementOptions);

  stripeAddressElement.on("ready", () => {
    const addressElement = document.querySelector<HTMLElement>(config.addressElementSelector);
    const staticStripeInputs: NodeListOf<HTMLInputElement> = document.querySelectorAll(".form-group--stripe");

    addressElement.parentElement.classList.toggle(config.loadingClass);

    staticStripeInputs.forEach((staticStripeInput) => {
      if (staticStripeInput.classList.contains("visible")) return;

      staticStripeInput.classList.add("visible");
    });
  });

  stripeAddressElement.on("change", (event) => {
    if (event.complete) {
      detailsRequest.firstName = event.value.firstName;
      detailsRequest.lastName = event.value.lastName;
      detailsRequest.phoneNumber = event.value.phone;

      // Extract potentially complete address
      const address = event.value.address;

      detailsRequest.addressLine1 = address.line1;
      detailsRequest.addressLine2 = address.line2;
      detailsRequest.townCity = address.city;
      detailsRequest.postalCode = address.postal_code;
      detailsRequest.country = address.country;

      trackEvent(ecommerceEvents.add_shipping_info);
    }
  });

  stripeAddressElement.mount(config.addressElementSelector);

  if (responseJson.amount > 0.0) {
    const paymentElement = elements.create("payment", paymentElementOptions);
    paymentElement.mount(config.paymentElementSelector);

    paymentElement.on("change", (event) => {
      if (event.complete) {
        trackEvent(ecommerceEvents.add_payment_info, { payment_type: event.value.type });
      }
    });
  } else {
    trackEvent(ecommerceEvents.add_payment_info, { payment_type: "FREE" });
  }
}

function checkDate(dobDay: number, dobMonth: number, dobYear: number, formElement: HTMLElement, errorMessage: string = undefined) {
  if (dobDay != null && dobMonth != null && dobYear != null) {
    try {
      const d = dobDay;
      const m = dobMonth;
      const y = dobYear;

      const currYr = new Date().getFullYear();
      const minYr = currYr - 100;

      // create the date object with the values sent in (month is zero based)
      var dt = new Date(y, m - 1, d, 0, 0, 0, 0);

      // get the month, day, and year from the object we just created
      var mon = dt.getMonth() + 1;
      var day = dt.getDate();
      var yr = dt.getFullYear();

      // if they match then the date is valid
      if (mon == m && yr == y && day == d && minYr <= y) {
        setDobInputsValidState(true, formElement, errorMessage);
      } else {
        setDobInputsValidState(false, formElement, errorMessage);
      }
    } catch (e) {
      setDobInputsValidState(false, formElement, errorMessage);
    }
  }
}

function setDobInputsValidState(state: boolean, formElement: HTMLElement, errorMessage: string = undefined) {  
  const invalidMessageWrapper: HTMLElement = formElement.querySelector(".field-validation-error");

  if(!state && errorMessage) {
    invalidMessageWrapper.innerHTML = errorMessage;
  }

  invalidMessageWrapper.style.display = state ? "none" : "block";

  formElement.querySelectorAll<HTMLInputElement>(".inline-date__inputs input").forEach((dobInput) => {
    dobInput.classList.add(state ? "valid" : "is-invalid");
    dobInput.classList.remove(state ? "is-invalid" : "valid");
  });

  const dobVal = formElement.querySelector<HTMLInputElement>(".dateValidatorInput");

  dobVal?.classList.add(state ? "valid" : "is-invalid");
  dobVal?.classList.remove(state ? "is-invalid" : "valid");
}

async function handleSubmit(e) {  
  e.preventDefault();
  
  setLoading(true);
  
  //Do we need to pay? Minimum balance is 30p
  const total = parseFloat(document.querySelector(config.orderTotalSelector)?.innerHTML ?? "0.00");

  const { error } = await elements.submit();

  if (error) {
    if (error.type === "validation_error") {
      var addressElementCodes = [
        "incomplete_first_name",
        "incomplete_last_name",
        "incomplete_address",
        "empty_phone_number",
        "incomplete_phone_number",
        "invalid_phone_number",
      ];

      setLoading(false);

      if (addressElementCodes.includes(error.code)) {
        scrollToElement(config.addressElementSelector);
        return;
      } else if (total > 0.0) {
        scrollToElement(config.paymentElementSelector);
        return;
      }
    } else if (error.type === "card_error" && total > 0.0) {
      showMessage(error.message);
    } else {
      showMessage("An unexpected error occurred.");
    }
  }

  const dateOfBirthElement = document.querySelector<HTMLElement>("[data-date-of-birth]");

  //check date of birth
  checkDate(detailsRequest.dobDay, detailsRequest.dobMonth, detailsRequest.dobYear, dateOfBirthElement);
    

  var invalidFields = document.querySelectorAll('.form-control.input-validation-error');

  if (invalidFields.length > 0 ) {
      scrollToElement(`#${invalidFields[0].id}`);      
      setLoading(false);
      return;
  }

  //validate extra field date elements
  const extraFieldsWrapper = document.querySelector(config.extraFieldsSelector);
  if(extraFieldsWrapper) {
    const requiredDateInputs = extraFieldsWrapper.querySelectorAll<HTMLInputElement>('[data-field-type="date"][data-required="required"]');

    requiredDateInputs.forEach(requiredDateInput => {
      const dobDayInputValue = requiredDateInput.querySelector<HTMLInputElement>('.inline-date__day').value;
      const dobMonthInputValue = requiredDateInput.querySelector<HTMLInputElement>('.inline-date__month').value;
      const dobYearInputValue = requiredDateInput.querySelector<HTMLInputElement>('.inline-date__year').value;
      
      checkDate(parseInt(dobDayInputValue), parseInt(dobMonthInputValue), parseInt(dobYearInputValue), requiredDateInput, `${requiredDateInput.dataset.fieldLabel} is required`);

    });
  }


  //Set address details here?
  let response: ApiResponseWithData<BasketResponse>;
  let apiUrl = constants.urls.basketV2.delivery_details;

  try {

    // Show payment processing message    
    startLoadingState(processingPaymentMessage, ".notice-overlay");

    response = await postJson<ApiResponseWithData<BasketResponse>>(apiUrl, detailsRequest);
    
  } catch (e) {
    showMessage("There was an error submitting personal details");
    setLoading(false);
    return;
  }

  if (!response.success) {
    const errors = response.errors
      .map((e) => {
        return e.code + ": " + e.message;
      })
      .join(", ");
    if (errors.indexOf("Date") > -1) {      
      scrollToElement(config.extraFieldsSelector);
    }

    // Reset validation spans to valid state
    document.querySelectorAll("span[data-valmsg-for]").forEach((span) => {
      span.textContent = "";
      span.classList.remove("field-validation-error");
      span.classList.add("field-validation-valid");
    });

    response.errors.forEach((e) => {
      const [label, errorMessage] = e.message.split(",");
      if (label && errorMessage) {
        const errorSpan = document.querySelector(`span[data-valmsg-for='${label.trim()}']`);
        if (errorSpan) {
          errorSpan.textContent = errorMessage.trim();
          errorSpan.classList.remove("field-validation-valid");
          errorSpan.classList.add("field-validation-error");
        }
      }
    });

    showMessage("There was an error submitting personal details");
    setLoading(false);
    return;
  }

  //Update payment intent to reflect any alterations to the order total
  //from reward points, discounts and vouchers
  const updateResponse = await fetch(constants.urls.payment.update, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });

  if (!updateResponse.ok) {
    const errMsg = "There was an error finalizing payment. A payment has not been taken.";
    logError(updateResponse.status, updateResponse.statusText, errMsg);
    showMessage(errMsg);
    setLoading(false);
    return;
  }

  var updateResponseJson: any = await updateResponse.json();

  const uri = new URL(config.redirectUri);
  uri.searchParams.append("order_number", updateResponseJson.orderId);

  if (total <= 0.0) {
    //Nothing to pay (validated server-side of course!)
    uri.searchParams.append("payment_intent_client_secret", elementOptions.clientSecret);
    window.location.assign(uri.href);
  } else if (total < 0.3) {
    //We are below the minimum payment allowed for the provider
    showMessage(
      "The total amount outstanding after discounts and vouchers, must be zero or greater than 30p in order to complete the checkout."
    );
  } else {
    //Attempt payment with the provider
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Make sure to change this to your payment completion page
        return_url: uri.toString(),
        payment_method_data: {
          billing_details: {
            //name: detailsRequest.firstName,
            name: detailsRequest.firstName + " " + detailsRequest.lastName,
            email: detailsRequest.email,
            phone: detailsRequest.phoneNumber,
            address: {
              line1: detailsRequest.addressLine1,
              line2: detailsRequest.addressLine2,
              city: detailsRequest.townCity,
              state: "",
              postal_code: detailsRequest.postalCode,
              country: detailsRequest.country,
            },
          },
        },
      },
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error.type === "card_error") {
      showMessage(error.message);
    } else {
      showMessage("An unexpected error occurred.");
    }
  }
  setLoading(false);
  endLoadingState(".notice-overlay");
}

function trackEvent(eventName, additionalInfo = {}) {
  var key = `${eventName}_track`;

  if (!window[key]) {
    var trackingData = JSON.parse(attribute(checkoutElement, attributes.orderTracking));

    trackingData = { ...trackingData, ...additionalInfo };

    trackEcommerceEvent({
      event: eventName,
      ecommerce: trackingData,
    });

    window[key] = true;

    setTimeout(() => {
      window[key] = undefined;
    }, 3000);
  }
}

function scrollToElement(selector) {
  const element = document.querySelector<HTMLElement>(selector);
  if (element) {
    let elementPosition = element.getBoundingClientRect();
    window.scrollTo({ top: elementPosition.top + window.scrollY - 150 });
  }
}

async function checkStatus() {
  const clientSecret = new URLSearchParams(window.location.search).get("payment_intent_client_secret");

  if (!clientSecret) {
    return;
  }

  const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);

  switch (paymentIntent.status) {
    case "succeeded":
      showMessage("Payment succeeded!", {trackError: false});
      break;
    case "processing":
      showMessage("Your payment is processing.", {trackError: false});
      break;
    case "requires_payment_method":
      showMessage("Your payment was not successful, please try again.");
      break;
    default:
      showMessage("Sorry, we could not process your payment.<br/>Please check your payment details and try again.");
      break;
  }
}

function showMessage(messageText, { hideOnTimeout = false, wrapperCssClass = "", trackError = true } = {}) {
  
  const paymentFailedContainer = document.querySelector(".payment-failed");  
  var messageElement = paymentFailedContainer.querySelector(".payment-failed__body");

  messageElement.innerHTML = `
      <p>${messageText}</p>
      <p>&nbsp;</p>
      <p>Our ticketing agents can help complete your purchase.</p>
      <p>Please connect with us through live chat or call <a href="tel:03445793019"><strong>0344 579 3019</strong></a>.</p>
      <p>We will get this sorted for you right away.</p>
  `;

  if(wrapperCssClass !== "") {  
    paymentFailedContainer.classList.add(wrapperCssClass);
  }

  paymentFailedContainer.classList.remove(classes.HIDDEN);

  if(trackError) {
    trackCheckoutEvent({
      message_name: "purchase_failed",
      message_text: messageText,
      message_type: "error",
      checkout_step: "payment"
    });
  }

  if(hideOnTimeout) {
    setTimeout(function () {
      paymentFailedContainer.classList.add(classes.HIDDEN);
      messageElement.innerHTML = "<p></p>";
    }, 10000);
  }
}
